import { Link } from "react-router";

export type OptionItemData = {
  title: string;
  description: string;
  image: string;
  weightRange?: string;
  finish?: string;
  features?: string[];
  link?: string;
};

/** Local assets from `public/` (e.g. `/woven.png`); external URLs unchanged. */
export function publicImageSrc(path: string) {
  if (path.startsWith("http://") || path.startsWith("https://")) return path;
  return path.startsWith("/") ? path : `/${path}`;
}

export function OptionCard({ option }: { option: OptionItemData }) {
  const src = publicImageSrc(option.image);
  const isExternal = option.image.startsWith("http://") || option.image.startsWith("https://");

  return (
    <div className="group bg-[#FBFAF7] rounded-2xl overflow-hidden border border-[#131312]/10 hover:border-[#131312]/10 transition-all hover:shadow-xl">
      <div className="aspect-[4/3] overflow-hidden bg-gray-100">
        <img
          src={src}
          alt={option.title}
          loading="lazy"
          decoding="async"
          referrerPolicy={isExternal ? "no-referrer" : undefined}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        />
      </div>
      <div className="p-6">
        <h3 className="font-semibold mb-2 group-hover:text-[#131312] transition-colors">{option.title}</h3>
        <p className="text-sm text-muted-foreground leading-relaxed">{option.description}</p>

        {/* Metadata section kept only for categories like paperboard */}
        {(option.weightRange || option.finish || (option.features && option.features.length > 0)) && (
          <div className="mt-6 space-y-4">
            {(option.weightRange || option.finish) && (
              <div className="grid grid-cols-2 gap-3">
                {option.weightRange && (
                  <div className="p-2 bg-[#FBFAF7] rounded border border-[#131312]/10">
                    <div className="text-[10px] text-muted-foreground uppercase tracking-wider mb-0.5">Weight</div>
                    <div className="font-semibold text-xs text-[#131312]">{option.weightRange}</div>
                  </div>
                )}
                {option.finish && (
                  <div className="p-2 bg-[#FBFAF7] rounded border border-[#131312]/10">
                    <div className="text-[10px] text-muted-foreground uppercase tracking-wider mb-0.5">Finish</div>
                    <div className="font-semibold text-xs text-[#131312]">{option.finish}</div>
                  </div>
                )}
              </div>
            )}
            {option.features && option.features.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {option.features.map((f, i) => (
                  <span key={i} className="px-2.5 py-1 bg-[#131312] text-white text-[10px] rounded-full font-bold tracking-tight shadow-sm uppercase backdrop-blur-[2px]">
                    {f}
                  </span>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

